import React, { useMemo } from 'react'
import { Form, Select } from 'antd'
import { useStore, useUnit } from 'effector-react'
import dayjs, { Dayjs } from 'dayjs'
import {
  $budgetPaymentForm,
  addBudgetPayment,
  addBudgetPaymentMounted,
  deleteBudgetPayment,
  deleteBudgetPaymentMounted,
  editBudgetPayment,
  editBudgetPaymentMounted,
  getBudgetPaymentById,
  getBudgetPaymentByIdMounted,
  setBudgetPaymentForm,
} from './model'
import { useBudgetPaymentModal } from './hooks'
import { $userProfile } from '../../../../../features/user'
import { transactionType } from '../../../../../libs/finSystem'
import {
  formatTransactionDateTime,
  parseNaiveDateTime,
} from '../../../../../libs/utils'
import { $walletAllowedList } from '../GeneralTransactionModal/model'
import { $params } from '../../../../../features/router'
import { $wallet as $currentWallet } from '../../details/model'
import { initialBudgetModalState } from './const'
import { ModalType } from '../types'
import { TransactionModalLayout } from '../TransactionModalLayout'
import { useTransactionForm } from '../useTransactionForm'
import {
  AmountWithCurrency,
  CommentField,
  DateTimeRow,
  ExchangeRateField,
  LockedTypeSelect,
  ProductSelect,
  TextField,
  WalletSelect,
  useDefaultCurrencyOnce,
} from '../transactionFields'

type Props = {
  isOpen: boolean
  onClose: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
  transactionId?: string
  type: ModalType
}

type FormValues = {
  walletId: string
  budgetItemId: string
  product: string
  recipient: string
  amount: string
  currency: string
  commission: string
  commissionCurrency: string
  exchangeRate: string
  date: Dayjs | null
  time: Dayjs | null
  comment: string
}

export const BudgetPaymentTransactionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCancel,
  transactionId,
  mode = 'create',
  type,
}) => {
  const [form] = Form.useForm<FormValues>()
  const { budgetItems } = useBudgetPaymentModal(isOpen)
  const allWallets = useUnit($walletAllowedList)
  const currentWallet = useUnit($currentWallet)
  const userProfile = useUnit($userProfile)
  const params = useStore($params)

  const walletId = Form.useWatch('walletId', form)

  useDefaultCurrencyOnce(form, 'currency', 'commissionCurrency')

  const fromWalletCurrencies = useMemo(() => {
    if (currentWallet.id) {
      return currentWallet.localBalances?.map((item) => item.currency) ?? []
    }
    const w = allWallets.find((item) => Number(item.id) === Number(walletId))
    return w?.currencies ?? []
  }, [allWallets, walletId, currentWallet])

  const initialWalletId =
    params?.walletID ||
    (type === 'wallet' && currentWallet.id ? String(currentWallet.id) : '')

  const {
    storeData: budgetPaymentForm,
    handleSubmit,
    handleSubmitAndCreateNew,
    handleCancel,
    handleDelete,
    isSubmitting,
    isDeleting,
    isLoadingForm,
  } = useTransactionForm({
    form,
    mode,
    transactionId,
    isOpen,
    onClose,
    onCancel,
    store: $budgetPaymentForm,
    initialState: initialBudgetModalState,
    setForm: setBudgetPaymentForm,
    mapStoreToForm: (data) => {
      const dt = parseNaiveDateTime(data.dateTime)
      return {
        walletId: String(data.walletId ?? ''),
        budgetItemId: String(data.budgetItemId ?? ''),
        product: data.product,
        recipient: data.recipient,
        amount: String(data.amount ?? ''),
        currency: data.currency,
        commission: String(data.commission ?? '0'),
        commissionCurrency: data.commissionCurrency,
        exchangeRate: String(data.exchangeRate ?? ''),
        date: dt ? dayjs(dt.date) : dayjs(),
        time: dt ? dayjs(dt.time, 'HH:mm:ss') : dayjs(),
        comment: data.comment,
      }
    },
    events: {
      addMount: addBudgetPaymentMounted,
      editMount: editBudgetPaymentMounted,
      deleteMount: deleteBudgetPaymentMounted,
      getByIdMount: getBudgetPaymentByIdMounted,
    },
    effects: {
      add: addBudgetPayment,
      edit: editBudgetPayment,
      delete: deleteBudgetPayment,
      getById: getBudgetPaymentById,
    },
    historyFields: { recipient: 'finSystem.recipient' },
    buildPayload: (values, { mode: m, transactionId: tid }) => ({
      ...$budgetPaymentForm.getState(),
      id: m === 'edit' ? tid : '',
      walletId: String(values.walletId ?? ''),
      budgetItemId: String(values.budgetItemId ?? ''),
      product: values.product,
      recipient: values.recipient,
      amount: values.amount,
      currency: values.currency,
      commission: values.commission || '0',
      commissionCurrency: values.commissionCurrency,
      exchangeRate: values.exchangeRate || '0',
      comment: values.comment ?? '',
      dateTime: formatTransactionDateTime(
        values.date!.format('YYYY-MM-DD'),
        values.time!.format('HH:mm:ss'),
      ),
    }),
  })

  return (
    <TransactionModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      title={`${mode === 'edit' ? 'Edit' : 'Create'} budget payment${
        budgetPaymentForm.id ? ` #${budgetPaymentForm.id}` : ''
      }`}
      mode={mode}
      creatorEmail={userProfile.email}
      isLoading={isLoadingForm}
      isSubmitting={isSubmitting}
      isDeleting={isDeleting}
      onSubmit={handleSubmit}
      onSubmitAndCreateNew={
        mode === 'create' ? handleSubmitAndCreateNew : undefined
      }
      onDelete={mode === 'edit' ? handleDelete : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          walletId: initialWalletId,
          commission: '0',
          date: dayjs(),
          time: dayjs(),
        }}
        requiredMark={false}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <WalletSelect
            name="walletId"
            label="Payment wallet"
            required
            disabled={type === 'wallet' || !!params?.walletID}
            style={{ flex: 1 }}
          />
          <LockedTypeSelect
            value={transactionType[1].value}
            options={transactionType}
            style={{ flex: 1 }}
          />
        </div>

        <Form.Item
          name="budgetItemId"
          label="Budget item"
          rules={[{ required: true, message: 'Budget item is required' }]}
        >
          <Select
            placeholder="Select budget item"
            showSearch
            optionFilterProp="label"
            filterSort={(a, b) =>
              String(a.label ?? '').localeCompare(
                String(b.label ?? ''),
                undefined,
                {
                  numeric: true,
                  sensitivity: 'base',
                },
              )
            }
            options={budgetItems.map((b) => ({ value: b.id, label: b.name }))}
          />
        </Form.Item>

        <ProductSelect name="product" required allowClear={false} />

        <TextField
          name="recipient"
          label="Receiver"
          placeholder="Receiver name or email"
          required
          historyScope="finSystem.recipient"
        />

        <div style={{ display: 'flex', gap: 12 }}>
          <AmountWithCurrency
            amountName="amount"
            currencyName="currency"
            amountLabel="Sent"
            currencies={fromWalletCurrencies}
            required
            allowNegative
            currencyRequired
            style={{ flex: 1 }}
          />
          <AmountWithCurrency
            amountName="commission"
            currencyName="commissionCurrency"
            amountLabel="Commission"
            currencies={fromWalletCurrencies}
            allowNegative
            currencyAllowClear
            style={{ flex: 1 }}
          />
        </div>

        <ExchangeRateField name="exchangeRate" />

        <DateTimeRow />

        <CommentField />
      </Form>
    </TransactionModalLayout>
  )
}
